import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useApi } from "../../../hooks/useApi"
import { Dashboard, DashboardData } from "../types/dashboard"

import { DashboardUpdateCreatePayload } from "../../../../types/macroApi"

export const useDashboardsQuery = () => {
  const { scientApi } = useApi()

  const { data, error, isLoading } = useQuery(
    ["dashboards"],
    () => scientApi.macro.getDashboards(),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      meta: {
        toasterErrorMessage: "Failed to fetch dashboards",
      },
    },
  )

  return {
    dashboards: data as Dashboard[],
    isLoading,
    error,
  }
}

export const useDashboardDataQuery = (dashboardId: number | null | undefined) => {
  const { scientApi } = useApi()

  const { data, error, isFetching } = useQuery(
    ["dashboard", dashboardId],
    () => scientApi.macro.getDashboardData(dashboardId!),
    {
      enabled: !!dashboardId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      meta: {
        toasterErrorMessage: "Failed to fetch dashboard data",
      },
    },
  )

  return {
    dashboardData: data as DashboardData,
    isFetching,
    error,
  }
}

export const useDashboardMutation = () => {
  const { scientApi } = useApi()
  const queryClient = useQueryClient()

  return useMutation<Dashboard, Error, DashboardUpdateCreatePayload>({
    mutationFn: (data: DashboardUpdateCreatePayload) => scientApi.macro.createDashboard(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["dashboards"])
    },
  })
}

export const useDashboardUpdateMutation = () => {
  const { scientApi } = useApi()
  const queryClient = useQueryClient()

  return useMutation<Dashboard, Error, { id: number; data: DashboardUpdateCreatePayload }>({
    mutationFn: ({ id, data }: { id: number; data: DashboardUpdateCreatePayload }) =>
      scientApi.macro.updateDashboard(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries(["dashboard", id])
      queryClient.invalidateQueries(["dashboards"])
    },
  })
}

export const useRunDashboardPredictionMutation = () => {
  const { scientApi } = useApi()
  const queryClient = useQueryClient()

  return useMutation<{ status: string }, Error, { id: number }>({
    mutationFn: ({ id }) => scientApi.macro.runDashboardPrediction(id),
    onSuccess: (_data, { id }) => {
      // Don't immediately invalidate - let the notification system handle it
      // The backend will send a notification when prediction is ready
      // User can manually refresh if they want to check
    },
  })
}

export const useDeleteDashboardMutation = () => {
  const { scientApi } = useApi()
  const queryClient = useQueryClient()

  return useMutation<void, Error, number>({
    mutationFn: (dashboardId: number) => scientApi.macro.deleteDashboard(dashboardId),
    onSuccess: () => {
      queryClient.invalidateQueries(["dashboards"])
    },
    meta: {
      toasterErrorMessage: "Failed to delete dashboard",
    },
  })
}
